import "./CustomTypeaheadInput.css";
import React from "react";
import PropTypes from "prop-types";
import { observable, computed, toJS } from "mobx";
import { observer, PropTypes as MobxPropTypes } from "mobx-react";
import { Typeahead } from "react-bootstrap-typeahead";
import isEmpty from "lodash/isEmpty";
import CustomError from "./CustomError";
import isFunction from "lodash/isFunction";
import isUndefined from "lodash/isUndefined";
import autobind from "autobind-decorator";
import ApiGetter from "shared/models/ApiGetter";
import classNames from "classnames";
import { CustomFormContext } from "./CustomForm";

@observer
class CustomTypeaheadInput extends React.Component {

    @observable serverOptions = [];
    @observable selected = [];
    @observable displaySearchOptions = false;

    @computed
    get renderableOptions() {
        let options = this.props.options.slice();
        return isEmpty(options) ? toJS(this.serverOptions) : options;
    }

    static contextType = CustomFormContext;

    static propTypes = {
        dispatcher: PropTypes.object.isRequired,
        fieldName: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        label: PropTypes.string,
        className: PropTypes.string,
        initialValue: PropTypes.string,
        options: MobxPropTypes.arrayOrObservableArray,
        endpoint: PropTypes.string,
        emptyLabel: PropTypes.node,
        placeholder: PropTypes.string,
        disabled: PropTypes.bool,
        maxVisible: PropTypes.number,
        outConverter: PropTypes.func,
        searchOptions: PropTypes.func,
        filterBy: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.func]),
        onOptionSelected: PropTypes.func,
        onBlur: PropTypes.func,
        onFocus: PropTypes.func,
        renderTypeaheadMenu: PropTypes.func
    };

    static defaultProps = {
        maxVisible: 3,
        //expected data format is a SelectOptionViewModel.cs
        options: [],
        initialValue: "",
        labelKey: "label",
        filterBy: ["label"],
        outConverter: option => option ? option.value : option,
        disabled: false,
        renderTypeaheadMenu: undefined
    };

    componentDidMount() {
        const dispatcher = this.props.dispatcher;
        let apiGetter = new ApiGetter(dispatcher);
        if (isEmpty(this.props.options.slice()) && !isEmpty(this.props.endpoint) && !isUndefined(dispatcher)) {
            apiGetter.fetch(this.props.endpoint,
                (res) => {
                    this.serverOptions = res;
                });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.value !== this.props.value) {
            this.selected = this.props.value ? [this.props.value] : [];
        }
    }

    @autobind
    handleChange(selectedOption, e) {
        this.selected = selectedOption ? [selectedOption] : [];

        if (!selectedOption) {
            return;
        }

        const value = this.props.outConverter(selectedOption);
        this.context.form.set(this.props.fieldName, value);
        if (isFunction(this.props.onOptionSelected)) {
            this.props.onOptionSelected(selectedOption, e);
        }
        this.displaySearchOptions = false;
    }

    @autobind
    handleBlur(e) {
        if (isEmpty(e.target.value) && !isEmpty(this.context.form.get(this.props.fieldName))) {
            this.context.form.set(this.props.fieldName, "");
        }
        if (isFunction(this.props.onBlur)) {
            this.props.onBlur(e);
        }
        this.displaySearchOptions = false;
    }

    @autobind
    handleFocus(e) {
        if (isFunction(this.props.onFocus)) {
            this.props.onFocus(e);
        }
        if (this.props.showOptionsOnInputSelect) {
            this.displaySearchOptions = true;
        }
    }

    @autobind
    handleKeyDown(e) {
        this.displaySearchOptions = true;
    }

    render() {
        const inputId = `typeahead-${this.props.fieldName}`;

        const errors = this.context.form.errors.get(this.props.fieldName);

        const containerClasses = classNames(
            "custom-typeahead",
            "input-container",
            {
                "has-errors": !isUndefined(errors) && !isEmpty(errors.slice())
            },
            this.props.className
        );

        return (
            <div className={containerClasses}>
                {!isEmpty(this.props.label) &&
                    <label htmlFor={inputId}>{this.props.label}</label>
                }
                <Typeahead
                    onKeyDown={this.handleKeyDown}
                    open={this.displaySearchOptions}
                    options={this.renderableOptions}
                    labelKey={this.props.labelKey}
                    maxResults={this.props.maxVisible}
                    filterBy={this.props.filterBy}
                    renderMenu={this.props.renderTypeaheadMenu}
                    placeholder={this.props.placeholder}
                    defaultInputValue={this.props.initialValue}
                    emptyLabel={this.props.emptyLabel && this.props.emptyLabel}
                    onBlur={this.handleBlur}
                    onFocus={this.handleFocus}
                    disabled={this.props.disabled}
                    // NOTE: The Typeahead library provides the ability to handle multiple selected options, but
                    // since we don't have that functionality on the site now we default to one
                    // selected option.
                    onChange={options => this.handleChange(options[0])}
                    selected={this.selected.slice()}
                    id={this.props.id}
                    // NOTE: The Typeahead library provides the ability to "display additional results", but for now
                    // we are disabling that functionality alltogether across anything that makes use of this component.
                    // If needed, it can easily be implemented.
                    paginate={false}
                />
                <CustomError errors={errors} />
            </div>
        );
    }
}

export default CustomTypeaheadInput;
